import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape, FormattedMessage } from 'react-intl';
import Fleet from '@pedidosya/web-fenix/icons/Fleet';
import isEmptyObject from '@commons/utils/object/isEmptyObject';
import { FLAGS } from '@commons/fwf/constants';
import FormatPrice from '@shared/containers/FormatPrice';
import PlusTag from '@shopDetails/components/ShopHeader/components/PlusTag';
import { getDeliveryAmount } from '@shopDetails/utils/plus';
import FlaggedComponent from '@funWithFlags/containers/FlaggedComponent';
import { Container, Column, Row, Label, Text, Loading } from '../components';
import messages from './messages';
import DeliveryContainer from './components/DeliveryContainer';

const Delivery = ({ intl: { formatMessage }, info, loading, plusSettings }) => {
  const isFreeShipping = useMemo(() => info?.shipping === 0, [info]);
  const isWithoutMinimum = useMemo(() => info?.min === 0, [info]);
  const isEmptyTime = useMemo(() => loading || isEmptyObject(info?.time), [
    info,
    loading,
  ]);
  const plusDeliveryAmount = useMemo(
    () => getDeliveryAmount(info.shipping, plusSettings),
    [info, plusSettings],
  );
  const showPlusTag = useMemo(() => Boolean(plusSettings) && !isFreeShipping, [
    plusSettings,
    isFreeShipping,
  ]);

  return (
    <Container>
      <Column>
        <Label>{formatMessage(messages.startIn)}</Label>
        <Text>
          {loading && <Loading />}
          {!isEmptyTime && (
            <FormattedMessage {...messages.deliveryTime} values={info.time} />
          )}
        </Text>
      </Column>
      <Column>
        <Label>{formatMessage(messages.delivery)}</Label>
        <Text>
          {loading && <Loading />}
          {!loading &&
            (showPlusTag ? (
              <DeliveryContainer>
                <PlusTag
                  isPickup
                  finalPricePlus={plusDeliveryAmount}
                  originalPricePlus={info.shipping}
                />
              </DeliveryContainer>
            ) : (
              <>
                {isFreeShipping && (
                  <FormattedMessage {...messages.shippingAmountFree} />
                )}
                {!isFreeShipping && (
                  <Row>
                    <FormatPrice value={info.shipping} />
                    {info.useLiveOrderTracking && (
                      <FlaggedComponent flag={FLAGS.FLEET_UTILISATION_DX}>
                        {isFleetUtilisationEnabled =>
                          isFleetUtilisationEnabled ? (
                            <Fleet size="medium" />
                          ) : null
                        }
                      </FlaggedComponent>
                    )}
                  </Row>
                )}
              </>
            ))}
        </Text>
      </Column>
      <Column>
        <Label>{formatMessage(messages.minimum)}</Label>
        <Text>
          {loading && <Loading />}
          {!loading && (
            <>
              {isWithoutMinimum && (
                <FormattedMessage {...messages.deliveryAmountNoMin} />
              )}
              {!isWithoutMinimum && <FormatPrice value={info.min} />}
            </>
          )}
        </Text>
      </Column>
    </Container>
  );
};

Delivery.propTypes = {
  info: PropTypes.shape({
    time: PropTypes.shape({
      maxMinutes: PropTypes.number,
      minMinutes: PropTypes.number,
    }),
    shipping: PropTypes.number,
    min: PropTypes.number,
    useLiveOrderTracking: PropTypes.bool,
  }),
  intl: intlShape.isRequired,
  loading: PropTypes.bool,
  plusSettings: PropTypes.object,
};

export default memo(injectIntl(Delivery));
